import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import messages from "components/messagesDescription";
import withMatchMediaProps from "@hocs/with-match-media-props";
import { fetchProductDetail, sendOfferEmail } from "actions/marketplaceActions";
import { Button, Loader } from "cxa-components";
import Switch from "react-switch";
import { showPopup } from "actions/popupActions";
import { injectIntl, FormattedMessage } from "react-intl";
import { renderAsHTML } from "../../utils";
import Breadcrumb from "./Breadcrumb";
import SearchBar from "./SearchBar";
import { withRouter } from "react-router-dom";
import CarouselContainer from "../common/global-carousel/CarouselContainer";
import { Link } from "react-router-dom";
import StandardMoney from "../common/StandardMoney";

const RedeemLocations = ({ locations }) => {
  return locations.map(location => {
    return (
      <div key={location.providerLocationId} className="product-section">
        <strong>
          {location.locationName && <span>{location.locationName}</span>}
          {location.street1 && <span>, {location.street1}</span>}
          {location.street2 && <span>, {location.street2}</span>}
          {location.country && <span>, {location.country}</span>}
          {location.postalCode && <span>, {location.postalCode}</span>}
          {location.phoneNumber && <span>, {location.phoneNumber}</span>}
          {location.emailAddress && <span>, {location.emailAddress}</span>}
        </strong>
      </div>
    );
  });
};

const ProductPage = ({
  productId,
  product,
  fetchProductDetail,
  sendOfferEmail,
  showPopup,
  intl,
  isFetching,
  ...props
}) => {
  const [switchStatus, setSwitch] = useState(false);

  const ProviderLink = props => (
    <Link to={`/marketplace/provider/${product.providerId}`} {...props} />
  );

  const images =
    product.secondaryImages && product.secondaryImages.includes(product.imageMain) === false
      ? product.secondaryImages.unshift(product.imageMain)
      : product.secondaryImages;

  useEffect(() => {
    fetchProductDetail(productId);
  }, []);

  const visitMerchantSite = () => {
    showPopup({
      element: renderAsHTML(
        "div",
        intl.formatMessage(messages.marketplace.visitMerchantSiteMessage)
      ),
      callback: () => {
        // eslint-disable-next-line security/detect-non-literal-fs-filename
        product.website && window.open(product.website);
      },
    });
  };

  const sentOfferCode = () => {
    sendOfferEmail(productId, intl);
  };

  {
    isFetching && <Loader />;
  }

  const StandardPrice = () => {
    const displayValue = (
      <StandardMoney value={product.standardPrice} currency={product.currency || "SGD"} />
    );
    const shouldDisplay = product.standardPrice && product.standardPrice > 0;
    return shouldDisplay ? (
      <tr>
        <td>{intl.formatMessage(messages.marketplace.retailPriceLabel)}:</td>
        <td>
          {product.standardPrice === product.sellingPrice ? (
            <strong>{displayValue}</strong>
          ) : (
            <s>{displayValue}</s>
          )}
        </td>
      </tr>
    ) : (
      ""
    );
  };

  const RetailPrice = () => {
    const shouldDisplay =
      product.sellingPrice &&
      product.sellingPrice > 0 &&
      product.sellingPrice !== product.standardPrice;
    return shouldDisplay ? (
      <tr>
        <td>{intl.formatMessage(messages.marketplace.discountedPriceLabel)}:</td>
        <td>
          <strong>
            <StandardMoney value={product.sellingPrice} currency={product.currency || "SGD"} />
          </strong>
        </td>
      </tr>
    ) : (
      ""
    );
  };

  return (
    <article>
      <div className="marketplace-container">
        <SearchBar {...props} />
        <Breadcrumb
          categoryTags={product.categoryTags}
          intl={intl}
          pageLabel={intl.formatMessage(messages.marketplace.offerDetailLabel)}
        />
        <header className="product-detail-header">
          <strong>{intl.formatMessage(messages.marketplace.offerDetailLabel)}:</strong>
        </header>

        <div className="product-container">
          <div className="product-summary">
            <section className="img-carousel">
              {images && (
                <CarouselContainer
                  images={product.secondaryImages}
                  hasArrow={true}
                  width={"20rem"}
                  height={"20rem"}
                  paginationPosition={{ left: "30%", top: "90%" }}
                />
              )}
            </section>

            <table>
              <tr>
                <td> {intl.formatMessage(messages.marketplace.offerIDLabel)}:</td>
                <td>
                  <strong>{product.productId}</strong>
                </td>
              </tr>
              <StandardPrice />
              <RetailPrice />
              <tr>
                <td>{intl.formatMessage(messages.marketplace.categoryLabel)}:</td>
                <td>
                  {Array.isArray(product.categoryTags) &&
                    product.categoryTags.map((category, index) => (
                      <strong key={category.categoryId}>
                        {intl.formatMessage({
                          id: category.categoryName,
                          defaultMessage: category.categoryName,
                        })}
                        {index < product.categoryTags.length - 1 && ", "}
                      </strong>
                    ))}
                </td>
              </tr>
            </table>
            {product.providerProductKey && (
              <section>
                <span className={`switch-text ${switchStatus ? "switch-text-on" : ""}`}>
                  {product.toggleButtonLabel
                    ? intl.formatMessage({
                        id: product.toggleButtonLabel,
                        defaultMessage: product.toggleButtonLabel,
                      })
                    : intl.formatMessage(messages.marketplace.showOfferCodeLabel)}
                </span>
                <br />
                <Switch
                  onChange={() => setSwitch(!switchStatus)}
                  checked={switchStatus}
                  activeBoxShadow="0 0 2px 3px #000000"
                  width={220}
                  uncheckedIcon={false}
                  onColor="#CAD1D9"
                  onHandleColor="#000000"
                />
                <br />
                <br />
                {switchStatus && (
                  <span className="offer-code">
                    <strong>{product.providerProductKey}</strong>
                  </span>
                )}
              </section>
            )}
            <section className="btn-container">
              {product.providerProductKey && product.allowToEmailOfferCode && (
                <Button primary style={{ borderRadius: "5px" }} onClick={sentOfferCode}>
                  {intl.formatMessage(messages.marketplace.emailOfferCodeLabel)}
                </Button>
              )}
              {product.website && (
                <Button primary style={{ borderRadius: "5px" }} onClick={visitMerchantSite}>
                  {intl.formatMessage(messages.marketplace.visitMerchantSiteLabel)}
                </Button>
              )}
            </section>
          </div>
          <div className="product-detail">
            <section>
              <h3>{product.productName}</h3>
              <p>
                <FormattedMessage {...messages.marketplace.byDescription} />{" "}
                <ProviderLink>
                  <strong>{product.providerName}</strong>
                </ProviderLink>
              </p>
            </section>
            {product.body && (
              <section>
                <h3>{intl.formatMessage(messages.marketplace.descriptionLabel)}</h3>
                <div
                  className="product-section"
                  dangerouslySetInnerHTML={{ __html: product.body }}
                />
              </section>
            )}
            {product.termsAndConditions && (
              <section>
                <h3>{intl.formatMessage(messages.marketplace.termsAndConditionsLabel)}</h3>
                <div
                  className="product-section"
                  dangerouslySetInnerHTML={{ __html: product.termsAndConditions }}
                />
              </section>
            )}
            {product.howToRedeem && (
              <section>
                <h3>{intl.formatMessage(messages.marketplace.howToRedeemLabel)}</h3>
                <div
                  className="product-section"
                  dangerouslySetInnerHTML={{ __html: product.howToRedeem }}
                />
              </section>
            )}
            {(product.locations || []).length > 0 && (
              <section>
                <h3>{intl.formatMessage(messages.marketplace.locationLabel)}</h3>
                <RedeemLocations locations={product.locations || []} />
              </section>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

ProductPage.propTypes = {
  productId: PropTypes.string,
  product: PropTypes.object.isRequired,
  isNarrowScreen: PropTypes.bool,
  fetchProductDetail: PropTypes.func.isRequired,
  sendOfferEmail: PropTypes.func.isRequired,
  showPopup: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  intl: PropTypes.object,
};

const mapStateToProps = ({ marketplace }, ownProps) => ({
  product: marketplace.selectedProduct,
  isFetching: marketplace.isFetching,
  productId: ownProps.match.params.productId,
});

const mapDispatchToProps = {
  fetchProductDetail,
  sendOfferEmail,
  showPopup,
};

export default withMatchMediaProps({
  isNarrowScreen: {
    maxWidth: 680,
  },
})(withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(ProductPage))));



// WEBPACK FOOTER //
// ./components/marketplace/ProductPage.jsx